import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { MagnifyingGlass, Gauge, Percent, Megaphone, ClipboardText, FileText, Calculator, ChartLineUp, Crosshair, Gear, SignOut, Buildings, Lightning, Target, Star, Brain, Plus, UsersThree, Newspaper, Handshake, EnvelopeOpen, House, PaperPlaneTilt, Quotes } from '@phosphor-icons/react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import LenderBadge from './LenderBadge';
import RateAlertBanner from './RateAlertBanner';
import SyncHealthBanner from './SyncHealthBanner';
import CreateLenderModal from './CreateLenderModal';

const RECENT_LENDER_DAYS = 30;
function isRecentlyAdded(lender) {
  if (!lender?.created_at) return false;
  const source = lender.source || '';
  if (source !== 'auto_outlook_folder' && source !== 'manual') return false;
  const ageMs = Date.now() - new Date(lender.created_at).getTime();
  return ageMs > 0 && ageMs < RECENT_LENDER_DAYS * 86400_000;
}

import { MODULES, moduleForPath } from '../lib/adminModules';
import { useLocation } from 'react-router-dom';
import { CaretDown, SquaresFour } from '@phosphor-icons/react';

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeModule = moduleForPath(pathname);
  const [openModules, setOpenModules] = useState({});
  const [q, setQ] = useState('');
  const [lenders, setLenders] = React.useState([]);
  const [syncStatus, setSyncStatus] = React.useState({ configured: false, connected: false });
  const [showCreateLender, setShowCreateLender] = useState(false);

  const refreshLenders = React.useCallback(async () => {
    try {
      const r = await api.get('/lenders');
      setLenders(r.data);
    } catch {
      /* ignore */
    }
  }, []);

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [l, s] = await Promise.all([
          api.get('/lenders'),
          api.get('/outlook/status'),
        ]);
        if (!active) return;
        setLenders(l.data);
        setSyncStatus(s.data);
      } catch {
        /* ignore */
      }
      // Capture the canonical public origin so every server-side email
      // CTA (digest, sync alerts, manual-review pings) links to the URL
      // Mat actually uses — even when the backend pod is hosted elsewhere.
      try {
        await api.post('/admin/public-origin', { origin: window.location.origin });
      } catch {
        /* non-fatal */
      }
    })();
    return () => { active = false; };
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate('/admin/search?q=' + encodeURIComponent(q.trim()));
  };

  return (
    <div className="min-h-screen bg-white text-zinc-950 flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r border-zinc-200 bg-white sticky top-0 h-screen" data-testid="sidebar">
        <div className="p-5 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-zinc-950 text-white flex items-center justify-center rounded-sm">
              <Lightning size={18} weight="fill" />
            </div>
            <div>
              <div className="font-heading text-[15px] font-semibold tracking-tight leading-none">Hello Mortgage</div>
              <div className="label-overline mt-1">Admin · Modules</div>
            </div>
          </div>
        </div>

        <nav className="p-3 space-y-0.5" data-testid="primary-nav">
          <NavLink to="/admin/dashboard" end data-testid="nav-dashboard" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-sm text-[13.5px] transition-all duration-150 ${isActive ? 'bg-zinc-950 text-white font-medium' : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-950'}`}>
            <SquaresFour size={17} /><span className="font-body">All modules</span>
          </NavLink>
          {MODULES.map((m) => {
            const active = activeModule?.id === m.id;
            const open = openModules[m.id] ?? active;
            return (
              <div key={m.id} className="pt-1" data-testid={`nav-module-${m.id}`}>
                <button type="button" onClick={() => setOpenModules({ ...openModules, [m.id]: !open })} data-testid={`nav-module-toggle-${m.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-[13.5px] transition-all duration-150 ${active ? 'text-zinc-950 font-medium' : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-950'}`}>
                  <m.icon size={17} weight={active ? 'fill' : 'regular'} />
                  <span className="font-body flex-1 text-left">{m.label}</span>
                  <CaretDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="ml-4 pl-3 border-l border-zinc-200 space-y-0.5 mb-1">
                    {m.items.map(({ to, label, icon: Icon, testid }) => (
                      <NavLink key={to} to={to} end data-testid={testid}
                        className={({ isActive }) => `flex items-center gap-2.5 px-3 py-1.5 rounded-sm text-[12.5px] transition-all duration-150 ${isActive ? 'bg-zinc-950 text-white font-medium' : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-950'}`}>
                        <Icon size={14} /><span className="font-body">{label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="px-5 mt-2 label-overline flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Buildings size={12} /> Lenders ({lenders.length})</span>
          <button
            type="button"
            onClick={() => setShowCreateLender(true)}
            data-testid="create-lender-button"
            title="Add a lender manually (no email rate sheets)"
            className="text-zinc-500 hover:text-zinc-950 transition-colors"
          >
            <Plus size={12} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5" data-testid="lenders-list">
          {lenders.map((l) => (
            <NavLink
              key={l.lender_id}
              to={`/admin/lenders/${l.lender_id}`}
              data-testid={`lender-link-${l.short_code}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-2 py-1.5 rounded-sm text-[12.5px] transition-colors ${
                  isActive ? 'bg-zinc-50 text-zinc-950' : 'text-zinc-600 hover:bg-zinc-50/70 hover:text-zinc-950'
                }`
              }
            >
              <LenderBadge name={l.name} size="sm" />
              <span className="truncate">{l.name}</span>
              {isRecentlyAdded(l) && (
                <span
                  className="ml-auto text-[8.5px] tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-700 border border-emerald-500/30"
                  title={l.source === 'auto_outlook_folder' ? 'Auto-created from a new Outlook folder — review buydown defaults' : 'Recently added — review buydown defaults'}
                  data-testid={`lender-new-badge-${l.short_code}`}
                >
                  NEW
                </span>
              )}
            </NavLink>
          ))}
        </div>

        <div className="p-3 border-t border-zinc-200">
          <NavLink
            to="/admin/settings"
            data-testid="nav-settings"
            className="flex items-center gap-2 px-2 py-2 text-xs text-zinc-600 hover:text-zinc-950"
          >
            <Gear size={14} /> Settings
          </NavLink>
          <div className="mt-2 flex items-center gap-2 px-2 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-7 w-7 rounded-full border border-zinc-200" />
            ) : (
              <div className="h-7 w-7 rounded-full bg-zinc-200" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-zinc-950 truncate font-medium">{user?.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="text-zinc-500 hover:text-zinc-950"
              title="Sign out"
            >
              <SignOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <SyncHealthBanner />
        <RateAlertBanner />
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 border-b border-zinc-200">
          <div className="flex items-center gap-3 px-5 lg:px-8 py-3">
            <form onSubmit={onSearch} className="flex-1 max-w-xl flex items-center gap-2 border border-zinc-200 bg-zinc-50/60 rounded-sm px-3 py-1.5 focus-within:border-zinc-400 transition">
              <MagnifyingGlass size={14} className="text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search rates, promos, policies, documents…"
                data-testid="global-search"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-500 font-body"
              />
              <span className="label-overline hidden sm:inline">⌘K</span>
            </form>
            <NavLink
              to="/admin/settings"
              data-testid="outlook-status-chip"
              className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-sm text-[11px] font-medium border ${
                syncStatus.connected
                  ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600'
                  : syncStatus.configured
                  ? 'bg-amber-500/10 border-amber-500/20 text-amber-600'
                  : 'bg-zinc-50 border-zinc-200 text-zinc-600'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${syncStatus.connected ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-400'}`} />
              <span className="uppercase tracking-[0.15em]">
                {syncStatus.connected ? 'Outlook live' : syncStatus.configured ? 'Connect Outlook' : 'Setup Outlook'}
              </span>
            </NavLink>
          </div>
        </header>
        <main className="flex-1 px-5 lg:px-8 py-6 lg:py-8" data-testid="main-content">
          {children}
        </main>
      </div>
      {showCreateLender && (
        <CreateLenderModal
          onClose={() => setShowCreateLender(false)}
          onCreated={async (lender) => {
            setShowCreateLender(false);
            await refreshLenders();
            navigate(`/admin/lenders/${lender.lender_id}`);
          }}
        />
      )}
    </div>
  );
}
